import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Maximize, Minimize } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { IAppState } from 'redux/store/types';
import { bindActionCreators, Dispatch } from 'redux';
import { setViewModeAction, toggleExpandCondensedViewModeAction, toggleRecordMaximizeAction } from 'redux/records/actions';
import { ViewMode } from 'redux/records/types';

// Split content into markdown and SVG parts
const processContentWithSVG = (content: string) => {
  const parts: Array<{ type: 'markdown' | 'svg'; content: string }> = [];
  let lastIndex = 0;

  // Find all SVG elements
  const svgRegex = /<svg[\s\S]*?<\/svg>/g;
  let match;

  while ((match = svgRegex.exec(content)) !== null) {
    // Add markdown content before SVG
    if (match.index > lastIndex) {
      const markdownContent = content.slice(lastIndex, match.index).trim();
      if (markdownContent) {
        parts.push({ type: 'markdown', content: markdownContent });
      }
    }

    // Add SVG content
    parts.push({ type: 'svg', content: match[0] });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining markdown content
  if (lastIndex < content.length) {
    const markdownContent = content.slice(lastIndex).trim();
    if (markdownContent) {
      parts.push({ type: 'markdown', content: markdownContent });
    }
  }

  return parts;
};

// Component that renders mixed markdown and SVG content
const MixedContent = React.memo(({ content }: { content: string }) => {
  const parts = processContentWithSVG(content);

  return (
    <>
      {parts.map((part, index) => {
        if (part.type === 'svg') {
          return <div key={index} dangerouslySetInnerHTML={{ __html: part.content }} style={{ margin: '1em 0' }} />;
        } else {
          return (
            <ReactMarkdown
              key={index}
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <p
                    style={{
                      margin: '0.5em 0',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text',
                    }}
                  >
                    {children}
                  </p>
                ),
                h1: ({ children }) => (
                  <h1
                    style={{
                      margin: '0.5em 0 0.25em 0',
                      fontSize: '1.6em',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2
                    style={{
                      margin: '0.5em 0 0.25em 0',
                      fontSize: '1.4em',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3
                    style={{
                      margin: '0.5em 0 0.25em 0',
                      fontSize: '1.2em',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </h3>
                ),
                ul: ({ children }) => (
                  <ul
                    style={{
                      margin: '5px 0',
                      paddingLeft: '2em',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol
                    style={{
                      margin: '5px 0',
                      paddingLeft: '1.5em',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li
                    style={{
                      margin: '0',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </li>
                ),
                code: ({ node, className, children, ...props }: any) => {
                  const match = /language-(\w+)/.exec(className || '');
                  const language = match ? match[1] : '';
                  const isCodeBlock = className && className.includes('language-');

                  if (isCodeBlock && language) {
                    return (
                      <div
                        style={{
                          backgroundColor: '#2d3748',
                          color: '#e2e8f0',
                          padding: '12px',
                          borderRadius: '6px',
                          margin: '10px 0',
                          fontSize: '0.9em',
                          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                          overflow: 'auto',
                          userSelect: 'text',
                          WebkitUserSelect: 'text',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.75em',
                            color: '#a0aec0',
                            marginBottom: '8px',
                            textTransform: 'uppercase',
                            fontWeight: 'bold',
                          }}
                        >
                          {language.toUpperCase()}
                        </div>
                        <pre
                          style={{
                            margin: 0,
                            padding: 0,
                            backgroundColor: 'transparent',
                            color: 'inherit',
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            userSelect: 'text',
                            WebkitUserSelect: 'text',
                          }}
                        >
                          <code {...props}>{children}</code>
                        </pre>
                      </div>
                    );
                  }

                  return (
                    <code
                      style={{
                        backgroundColor: '#f7fafc',
                        color: '#2d3748',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontSize: '0.9em',
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                      }}
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                table: ({ children }) => (
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      margin: '10px 0',
                      width: '100%',
                      fontSize: '0.9em',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </table>
                ),
                thead: ({ children }) => (
                  <thead
                    style={{
                      backgroundColor: '#f5f5f5',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </thead>
                ),
                tbody: ({ children }) => (
                  <tbody
                    style={{
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr
                    style={{
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th
                    style={{
                      border: '1px solid #ddd',
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontWeight: 'bold',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td
                    style={{
                      border: '1px solid #ddd',
                      padding: '8px 12px',
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                    }}
                  >
                    {children}
                  </td>
                ),
              }}
            >
              {part.content}
            </ReactMarkdown>
          );
        }
      })}
    </>
  );
});

class CollapsibleText extends React.Component<CollapsibleTextProps> {
  private timerInterval: NodeJS.Timeout | null = null;
  private workingOnItRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    // Only start timer if this component is showing the "working on it" message
    if (!this.props.non_ai && !this.props.aiText) {
      this.startTimer();
      setTimeout(() => this.focusWorkingOnIt(), 1000);
    }
  }

  componentDidUpdate(prevProps: CollapsibleTextProps) {
    // Only start timer if this component is showing the "working on it" message
    if (!this.props.non_ai && !this.props.aiText && prevProps.aiText) {
      this.startTimer();
      setTimeout(() => this.focusWorkingOnIt(), 1000);
    }
  }

  startTimer() {
    this.timerInterval = setInterval(() => {
      const timerId = this.props.ownerSubrecordId || this.props.ownerRecordId;
      if (this.props.processingStartTimes[timerId]) this.forceUpdate();
    }, 1000);
  }

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  getElapsedSeconds = (): number => {
    const timerId = this.props.ownerSubrecordId || this.props.ownerRecordId;
    const startTime = this.props.processingStartTimes[timerId];
    return startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
  };

  render() {
    const {
      userText,
      aiText,
      interim,
      t,
      viewMode,
      toggleExpandCondensedViewModeAction,
      toggleRecordMaximizeAction,
      currentRecordId,
      ownerRecordId,
      ownerSubrecordId,
      maximizedRecords,
    } = this.props;
    const collapseText = currentRecordId !== ownerRecordId || viewMode === ViewMode.SHOW_CONDENSED_RECORDS_VIEW;
    const recordOrSubrecordId = ownerSubrecordId || ownerRecordId;
    const isMaximized = maximizedRecords[recordOrSubrecordId] || false;

    const toggleMoreLessButton = (
      <button className={`toggle-button ${(currentRecordId === ownerRecordId && 'current') || ''}`} onClick={toggleExpandCondensedViewModeAction}>
        {collapseText ? (
          <>
            <ChevronDown size={14} />
            {t('common.showMore')}
          </>
        ) : (
          <>
            <ChevronUp size={14} />
            {t('common.showLess')}
          </>
        )}
      </button>
    );

    const maximizeButton = (
      <button
        className='maximize-button'
        onClick={() => toggleRecordMaximizeAction(recordOrSubrecordId)}
        title={isMaximized ? t('common.restore') : t('common.maximize')}
      >
        {isMaximized ? <Minimize size={16} /> : <Maximize size={16} />}
      </button>
    );

    const textClassName = collapseText ? ' hidden' : '';
    const containerClassName = isMaximized ? 'collapsible-text maximized' : 'collapsible-text';

    return (
      <div className={containerClassName}>
        {!collapseText && (
          <div className='button-group'>
            {toggleMoreLessButton}
            {maximizeButton}
          </div>
        )}
        <div className={`user-text${textClassName}`}>{String(userText)}</div>
        {aiText && (
          <div className={`ai-text${textClassName}`} ref={this.workingOnItRef}>
            <MixedContent content={String(aiText)} />
          </div>
        )}
        {/* TODO: Groundwork for interim response, letting the user know that the LLM understood the query. */}
        {(!aiText || interim) && !this.props.non_ai && (
          <span className='onroute'>
            {interim} {t('common.workingOnIt')} ({this.getElapsedSeconds()}s)
          </span>
        )}
        {collapseText && <div className='button-group'>{toggleMoreLessButton}</div>}
      </div>
    );
  }

  focusWorkingOnIt = (): void => {
    if (this.workingOnItRef.current) {
      this.workingOnItRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      this.workingOnItRef.current.focus();
    }
  };
}

interface CollapsibleTextProps {
  ownerRecordId: string;
  ownerSubrecordId?: string;
  currentRecordId: string;
  userText: string | String;
  aiText: string | String | null;
  interim?: boolean;
  non_ai?: boolean;
  viewMode: ViewMode;
  processingStartTimes: { [recordOrSubrecordId: string]: number };
  toggleExpandCondensedViewModeAction: typeof toggleExpandCondensedViewModeAction;
  maximizedRecords: { [recordOrSubrecordId: string]: boolean };
  toggleRecordMaximizeAction: typeof toggleRecordMaximizeAction;
  t: (key: string, options?: any) => string;
}

// Maps required state from Redux store to component props
const mapStateToProps = (state: IAppState) => ({
  currentRecordId: state.records.currentRecordId as string,
  viewMode: state.records.viewMode,
  processingStartTimes: state.records.processingStartTimes,
  maximizedRecords: state.records.maximizedRecords,
});

// Map Redux actions to component props
const mapDispatchToProps = (dispatch: Dispatch<any>) =>
  bindActionCreators(
    {
      setViewModeAction,
      toggleExpandCondensedViewModeAction,
      toggleRecordMaximizeAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(CollapsibleText));
